import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import * as echarts from 'echarts/core';
import { BarChart, LineChart, ScatterChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import type { EChartsOption } from 'echarts';
import { i18n } from 'i18n';
import { useEffect, useMemo, useRef } from 'react';
import { ChartModel, formatValue } from './chartSpec';

// Register only what the builder can produce — the full echarts bundle is several times this size.
echarts.use([
  BarChart, LineChart, ScatterChart,
  GridComponent, TooltipComponent, LegendComponent, TitleComponent,
  CanvasRenderer,
]);

/**
 * Categorical palette. The slot ordering is the colour-blind-safety mechanism, not decoration:
 * colours are assigned in fixed order and never cycled, and this order was validated for adjacent
 * pair separation under protanopia/deuteranopia/tritanopia in both light and dark.
 */
const SERIES_LIGHT = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100'];
const SERIES_DARK = ['#3987e5', '#d95926', '#199e70', '#c98500'];

export interface ChartRendererProps {
  model: ChartModel | null;
  type: 'column' | 'line' | 'area' | 'scatter';
  stacked: boolean;
  /** Compact drops axis labels and the legend for small dashboard tiles. */
  compact?: boolean;
}

export default function ChartRenderer({
  model, type, stacked, compact = false,
}: ChartRendererProps) {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);

  const isDark = theme.palette.mode === 'dark';
  const palette = isDark ? SERIES_DARK : SERIES_LIGHT;
  const axisInk = theme.palette.text.secondary;
  const gridInk = theme.palette.divider;
  const surface = theme.palette.background.paper;

  const option = useMemo<EChartsOption | null>(() => {
    if (!model) return null;

    // The legend sits under the plot; without room reserved for it, it lands on the axis labels.
    const hasLegend = !compact && model.series.length > 1;
    const gridBottom = hasLegend ? 36 : 8;
    const legend = (icon: string) => (hasLegend ? {
      type: 'scroll' as const, bottom: 0, textStyle: { color: axisInk }, pageTextStyle: { color: axisInk }, icon, itemHeight: 8,
    } : undefined);

    const base: EChartsOption = {
      color: palette,
      backgroundColor: 'transparent',
      animation: false,
      grid: {
        left: compact ? 8 : 12,
        right: compact ? 8 : 16,
        top: compact ? 8 : 16,
        bottom: compact ? 4 : gridBottom,
        containLabel: true,
      },
      tooltip: {
        trigger: model.kind === 'scatter' ? 'item' : 'axis',
        confine: true,
        axisPointer: { type: type === 'column' ? 'shadow' : 'line' },
      },
      textStyle: { fontFamily: theme.typography.fontFamily },
    };

    if (model.kind === 'scatter') {
      return {
        ...base,
        legend: legend('roundRect'),
        xAxis: {
          type: 'value',
          name: compact ? undefined : [model.xLabel, model.xUnit && `(${model.xUnit})`].filter(Boolean).join(' '),
          nameLocation: 'middle',
          nameGap: 26,
          nameTextStyle: { color: axisInk },
          axisLabel: { color: axisInk, formatter: (v: number) => formatValue(v) },
          axisLine: { lineStyle: { color: gridInk } },
          splitLine: { lineStyle: { color: gridInk, type: 'solid' } },
        },
        yAxis: {
          type: 'value',
          name: compact ? undefined : [model.yLabel, model.yUnit && `(${model.yUnit})`].filter(Boolean).join(' '),
          nameTextStyle: { color: axisInk },
          axisLabel: { color: axisInk, formatter: (v: number) => formatValue(v) },
          axisLine: { show: false },
          splitLine: { lineStyle: { color: gridInk, type: 'solid' } },
        },
        series: model.series.map((s) => ({
          type: 'scatter' as const,
          name: s.name,
          symbolSize: compact ? 6 : 10,
          // A 2px ring in the surface colour keeps overlapping points readable.
          itemStyle: { borderColor: surface, borderWidth: 2 },
          data: s.points.map((p) => [p.x, p.y, p.label]),
          tooltip: {
            formatter: (params: any) => {
              const [x, y, label] = params.value as [number, number, string];
              return `<strong>${label}</strong><br/>${model.xLabel}: ${formatValue(x)} ${model.xUnit ?? ''}`
                + `<br/>${model.yLabel}: ${formatValue(y)} ${model.yUnit ?? ''}`;
            },
          },
        })),
      };
    }

    const multi = model.series.length > 1;
    const doStack = stacked && type === 'column' && multi;

    return {
      ...base,
      legend: legend(type === 'line' ? 'roundRect' : 'rect'),
      xAxis: {
        type: 'category',
        data: model.buckets,
        axisLabel: { color: axisInk, hideOverlap: true },
        axisLine: { lineStyle: { color: gridInk } },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        name: compact || !model.unit ? undefined : model.unit,
        nameTextStyle: { color: axisInk, align: 'right' },
        axisLabel: { color: axisInk, formatter: (v: number) => formatValue(v) },
        axisLine: { show: false },
        splitLine: { lineStyle: { color: gridInk, type: 'solid' } },
      },
      series: model.series.map((s, i) => {
        const colour = palette[i % palette.length];
        if (type === 'column') {
          return {
            type: 'bar' as const,
            name: s.name,
            stack: doStack ? 'total' : undefined,
            barMaxWidth: compact ? 16 : 24,
            // 4px rounded data-end, square at the baseline.
            itemStyle: doStack ? undefined : { borderRadius: [4, 4, 0, 0] },
            data: s.values,
          };
        }
        return {
          type: 'line' as const,
          name: s.name,
          smooth: false,
          showSymbol: false,
          lineStyle: { width: 2 },
          areaStyle: type === 'area' ? { opacity: 0.1 } : undefined,
          emphasis: { focus: 'series' as const },
          itemStyle: { color: colour },
          data: s.values,
        };
      }),
    };
  }, [model, type, stacked, compact, palette, axisInk, gridInk, surface, theme.typography.fontFamily]);

  useEffect(() => {
    if (!containerRef.current) return undefined;

    if (!chartRef.current) {
      chartRef.current = echarts.init(containerRef.current, undefined, { renderer: 'canvas' });
    }
    const chart = chartRef.current;

    if (option) {
      // notMerge, so removing a series actually removes it rather than leaving a ghost.
      chart.setOption(option, true);
    } else {
      chart.clear();
    }

    const observer = new ResizeObserver(() => chart.resize());
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [option]);

  useEffect(() => () => {
    chartRef.current?.dispose();
    chartRef.current = null;
  }, []);

  return (
    <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
      <Box ref={containerRef} sx={{ height: '100%', width: '100%' }} />
      {!model && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            p: 3,
            textAlign: 'center',
            pointerEvents: 'none',
          }}
        >
          <Typography variant="subtitle2" color="text.secondary">
            {i18n._('Nothing plotted yet')}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ maxWidth: '34ch' }}>
            {i18n._('Add a date or dimension to the X axis, and a measure to the Y axis.')}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
